import { BASE_URL, get, put, deleteRequest, fetchFile } from "./Common";

export interface IPlacementWorksheetLabel {
  id: number;
  label: string;
}

export interface IPlacementWorksheetColors {
  newResidentColor: string | null;
  renewingResidentColor: string | null;
  renewalTransferResidentColor: string | null;
}

export interface IPlacementWorksheetSettings {
  labels: IPlacementWorksheetLabel[];
  colors: IPlacementWorksheetColors;
}

export interface IPlacementWorksheetUnit {
  unitId: number;
  comment: string | null;
  unitNumber: string;
  occupants: IPlacementWorksheetUnitOccupant[];
}

export interface IPlacementWorksheetUnitOccupant {
  id: string;
  firstName: string;
  lastName: string;
  roomLabelId: number | null;
  status: "new" | "renewing";
  lifestyleQuestions: any[];
  gender: string;
  l4Id: number | null;
}

export interface IPlacementWorksheetOccupants {
  assignedUnits: IPlacementWorksheetUnit[];
  unassignedUnits: IPlacementWorksheetUnit[];
}

export interface IPlacementWorksheetRes {
  matchGroupAtLimit: boolean;
  settings: IPlacementWorksheetSettings;
  occupants: IPlacementWorksheetOccupants;
}

export const fetchPlacementWorksheet = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet`;

  const httpResponse = await get<IPlacementWorksheetRes>(url, authToken);

  return httpResponse.parsedBody;
};

export const fetchPlacementReport = async (l1Id: number,
  l2Id: number,
  l3Id: number,
  authToken: string) => {
    const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/report.csv`;
  return await fetchFile(url, authToken);
};


interface IPutPlacementWorksheetSettingsPayload
  extends IPlacementWorksheetColors {
  labelsToUpdate?: IPlacementWorksheetLabel[];
}

export const putPlacementWorksheetSettings = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  payload: Partial<IPutPlacementWorksheetSettingsPayload>,
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/settings`;
  const httpResponse = await put<IPlacementWorksheetRes>(
    url,
    authToken,
    undefined,
    payload
  );

  return httpResponse.parsedBody;
};

export const putPlacementWorksheetOccupants = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  unitId: number,
  overrideGenderCheck: boolean,
  payload: string[],
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/unit/${unitId}/occupants`;
  const httpResponse = await put(url, authToken, undefined, {
    unitId,
    overrideGenderCheck,
    occupants: payload,
  });

  return httpResponse.parsedBody;
};

export const deletePlacementWorksheetUnitOccupant = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  unitId: number,
  membershipId: string,
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/unit/${unitId}/occupants/${membershipId}`;
  const httpResponse = await deleteRequest(url, authToken);

  return httpResponse.parsedBody;
};

export const deletePlacementWorksheetRoomOccupant = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  unitId: number,
  membershipId: string,
  roomLabelId: number,
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/unit/${unitId}/occupants/${membershipId}/room/${roomLabelId}`;
  const httpResponse = await deleteRequest(url, authToken);

  return httpResponse.parsedBody;
};

export const putPlacementWorksheetOccupantRoom = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  unitId: number,
  membershipId: string,
  roomLabelId: number | string,
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/unit/${unitId}/occupants/${membershipId}/room/${roomLabelId}`;
  const httpResponse = await put(url, authToken);

  return httpResponse.parsedBody;
};

export const putPlacementWorksheetSettingsUnitNumber = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  unitId: number,
  unitNumber: string,
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/unit/${unitId}/unitNumber`;
  const payload = {
    unitId,
    unitNumber,
  };
  const httpResponse = await put<any>(url, authToken, undefined, payload);

  return httpResponse.parsedBody;
};

export const putPlacementWorksheetSettingsUnitComment = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  unitId: number,
  comment: string | null,
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/unit/${unitId}/comment`;
  const payload = {
    unitId,
    comment,
  };
  const httpResponse = await put<any>(url, authToken, undefined, payload);

  return httpResponse.parsedBody;
};

export const putPlacementWorksheetSettingsUnitNumberClear = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  unitIds: number[],
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/units/unitNumber/clear`;
  const payload = {
    unitIds,
  };
  const httpResponse = await put<any>(url, authToken, undefined, payload);

  return httpResponse.parsedBody;
};

export const putPlacementWorksheetOccupantsClear = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  unitIds: number[],
  authToken: string
) => {
  const url = `${BASE_URL}/v1/network/${l1Id}/subnetwork/${l2Id}/unitType/${l3Id}/placement_worksheet/units/occupants/clear`;
  const payload = {
    unitIds,
  };
  const httpResponse = await put<any>(url, authToken, undefined, payload);

  return httpResponse.parsedBody;
};
